import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';
import type { GridStackWidget } from 'gridstack';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { projectSlice } from '../../redux/ProjectSlice';
import type { AppDispatch, RootState } from '../../redux/Store';
import type { DriveUnitPortOccupancy } from '../../redux/ApplicationSlice';
import { buildPersistableDashboardContext, type PersistableDashboardContext } from '../../utils/dashboardWidgetUtils';
import { mergeDashboardWidgetLayouts, parseDashboardWidgetContent } from '../../utils/sensor-stream-tracker';
import type { DashboardWidgetContent } from './dashboardTypes';

/** Stable empty drive-unit port list for selectors and fallbacks. */
export const EMPTY_DRIVE_UNIT_PORTS: DriveUnitPortOccupancy[] = [];

export const selectDriveUnitPorts = (state: RootState) => state.application?.driveUnitPorts ?? EMPTY_DRIVE_UNIT_PORTS;

const selectLoadedProjectId = (state: RootState) => state.project.loadedProject?.id ?? '';

function selectEmptyDriveUnitPorts(): DriveUnitPortOccupancy[] {
  return EMPTY_DRIVE_UNIT_PORTS;
}

const driveUnitPortsSelectorByProjectId = new Map<string, ReturnType<typeof makeSelectDriveUnitPortsForProject>>();

function makeSelectDriveUnitPortsForProject(projectId: string) {
  return createSelector([selectDriveUnitPorts, selectLoadedProjectId], (driveUnitPorts, loadedProjectId) =>
    loadedProjectId === projectId ? driveUnitPorts : EMPTY_DRIVE_UNIT_PORTS
  );
}

function getSelectDriveUnitPortsForProject(projectId: string) {
  let selector = driveUnitPortsSelectorByProjectId.get(projectId);
  if (!selector) {
    selector = makeSelectDriveUnitPortsForProject(projectId);
    driveUnitPortsSelectorByProjectId.set(projectId, selector);
  }
  return selector;
}

/** Live registry ports when `projectId` is the loaded project; otherwise {@link EMPTY_DRIVE_UNIT_PORTS}. */
export function selectDriveUnitPortsForProject(state: RootState, projectId: string): DriveUnitPortOccupancy[] {
  return getSelectDriveUnitPortsForProject(projectId)(state);
}

/** Memoized drive-unit ports for export/duplicate when `projectId` is the loaded project. */
export function useDriveUnitPortsForProject(projectId: string | undefined): DriveUnitPortOccupancy[] {
  const selector = useMemo(() => (projectId ? getSelectDriveUnitPortsForProject(projectId) : selectEmptyDriveUnitPorts), [projectId]);
  return useSelector(selector);
}

/** Memoized per-project drive-unit ports map for bulk export/duplicate actions. */
export function useDriveUnitPortsByProjectIds(projectIds: readonly string[]): Readonly<Record<string, DriveUnitPortOccupancy[]>> {
  const loadedProjectId = useSelector(selectLoadedProjectId);
  const driveUnitPorts = useSelector(selectDriveUnitPorts);

  return useMemo(() => {
    const map: Record<string, DriveUnitPortOccupancy[]> = {};
    for (const projectId of projectIds) {
      map[projectId] = projectId === loadedProjectId ? driveUnitPorts : EMPTY_DRIVE_UNIT_PORTS;
    }
    return map;
  }, [projectIds, loadedProjectId, driveUnitPorts]);
}

const selectLoadedProjectCode = (state: RootState) => state.project.loadedProject?.program?.code ?? '';

export const selectPersistableDashboardContext = createSelector(
  [selectDriveUnitPorts, selectLoadedProjectCode],
  (driveUnitPorts, projectCode): PersistableDashboardContext | undefined => buildPersistableDashboardContext(driveUnitPorts, projectCode)
);

type LayoutMetaSync = (layout: GridStackWidget[]) => void;
type MetaLayoutGetter = () => GridStackWidget[];

let registeredLayoutMetaSync: LayoutMetaSync | undefined;
let registeredMetaLayoutGetter: MetaLayoutGetter | undefined;

/**
 * Registers a callback that mirrors layout `content` into GridStack's in-memory meta map.
 * `GridStackProvider` registers on mount so widget and configure-panel saves stay in sync
 * even when callers are outside React context.
 */
export function registerDashboardLayoutMetaMapSync(sync: LayoutMetaSync | undefined): void {
  registeredLayoutMetaSync = sync;
}

/**
 * Registers a snapshot of GridStack's in-memory widget meta map.
 * `useSyncDataStreamKeysFromLayout` merges this over Redux layout so `set_request_list`
 * reflects toolbar edits before the project layout state catches up.
 */
export function registerDashboardMetaLayoutGetter(getter: MetaLayoutGetter | undefined): void {
  registeredMetaLayoutGetter = getter;
}

/** Redux project layout with GridStack meta winning per widget id (for streaming sync). */
export function getMergedDashboardLayoutForStreaming(projectLayout: GridStackWidget[]): GridStackWidget[] {
  return mergeDashboardWidgetLayouts(projectLayout, registeredMetaLayoutGetter?.() ?? []);
}

/**
 * Copies widget `content` from a layout into the raw meta map (position/size unchanged).
 */
export function syncRawWidgetMetaMapFromLayout(setMap: Dispatch<SetStateAction<Map<string, GridStackWidget>>>, layout: GridStackWidget[]): void {
  setMap((prev) => {
    let newMap: Map<string, GridStackWidget> | null = null;

    for (const widget of layout) {
      if (!widget.id || !widget.content) continue;
      const existing = prev.get(widget.id);
      if (!existing || existing.content === widget.content) continue;
      if (!newMap) newMap = new Map(prev);
      newMap.set(widget.id, { ...existing, content: widget.content });
    }

    return newMap ?? prev;
  });
}

/** Persists dashboard layout to Redux and keeps GridStack meta map in sync when registered. */
export function persistDashboardLayoutToProject(
  dispatch: AppDispatch,
  nextLayout: GridStackWidget[],
  persistContext?: PersistableDashboardContext
): void {
  registeredLayoutMetaSync?.(nextLayout);
  dispatch(projectSlice.actions.onUpdateLastModified(new Date().toISOString()));
  dispatch(projectSlice.actions.onSaveLayout(persistContext ? { layout: nextLayout, persistContext } : nextLayout));
}

/**
 * Applies a content merge to one widget in a dashboard layout array.
 * Returns null when the widget is missing or has no parseable content.
 */
export function updateWidgetInDashboardLayout(
  layout: GridStackWidget[],
  widgetId: string,
  merge: (content: DashboardWidgetContent) => DashboardWidgetContent
): GridStackWidget[] | null {
  if (!layout.some((widget) => widget.id === widgetId && widget.content)) return null;

  return layout
    .map((widget) => {
      if (widget.id !== widgetId || !widget.content) return widget;
      const content = parseDashboardWidgetContent(widget.content);
      if (!content) return widget;
      return { ...widget, content: JSON.stringify(merge(content)) };
    })
    .filter((w): w is GridStackWidget => Boolean(w));
}

/**
 * Keeps GridStack's in-memory widget metadata in sync when widget content changes.
 * GridStackRender reads from this map; grid move/resize persists it back to Redux.
 */
export function updateRawWidgetMetaMapContent(
  setMap: Dispatch<SetStateAction<Map<string, GridStackWidget>>>,
  widgetId: string,
  merge: (content: DashboardWidgetContent) => DashboardWidgetContent
): void {
  setMap((prev) => {
    const widget = prev.get(widgetId);
    if (!widget?.content) return prev;
    const content = parseDashboardWidgetContent(widget.content);
    if (!content) return prev;

    const newMap = new Map(prev);
    newMap.set(widgetId, { ...widget, content: JSON.stringify(merge(content)) });
    return newMap;
  });
}
